import { useRef, useState } from 'react'
import { CheckCircle2, Download, FileUp, TriangleAlert } from 'lucide-react'
import { Badge, Button, ErrorNote, InfoNote } from '@/components/ui'
import { Modal } from '@/components/Modal'
import { useToast } from '@/components/Toast'
import { reconcileOrders } from '@/lib/api'
import { authErrorMessage } from '@/lib/authErrors'
import {
  MATCH_LABELS,
  readSwishReport,
  sampleSwishReport,
  type KnownOrder,
  type ReportResult,
  type ReportRow,
} from '@shared/swishReport'
import { formatSek } from '@/lib/format'
import { cn } from '@/lib/cn'

/**
 * Avstämning mot en uppladdad Swish-rapport.
 *
 * Filen tolkas här i webbläsaren och lämnar aldrig enheten. Rapporten
 * innehåller namn på alla som swishat föreningen den perioden, även de som
 * inte har med Lirahäftet att göra, och den datan har servern ingen
 * anledning att ta emot. Bara referenserna och beloppen skickas vidare.
 *
 * Ingenting bockas av förrän man sett förhandsvisningen. En felaktig
 * avbockning skickar ut häften för en betalning som aldrig kommit in, och
 * det upptäcks först vid bokslutet.
 */
export function ReconcileModal({
  orders,
  isTest = false,
  onClose,
}: {
  orders: KnownOrder[]
  /** Lagets beställningar är provdata, så en exempelrapport erbjuds. */
  isTest?: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const inputRef = useRef<HTMLInputElement>(null)

  const [fileName, setFileName] = useState<string | null>(null)
  const [result, setResult] = useState<ReportResult | null>(null)
  const [readError, setReadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function handleFile(file: File) {
    setReadError(null)
    setResult(null)
    setFileName(file.name)

    try {
      const text = await file.text()
      setResult(readSwishReport(text, orders))
    } catch {
      setReadError('Filen gick inte att läsa. Kontrollera att den är sparad som CSV.')
    }
  }

  async function handleSettle() {
    if (!result?.readyToSettle.length) return
    setBusy(true)
    try {
      const byReference = new Map(
        result.rows
          .filter((row) => row.status === 'matched')
          .map((row) => [row.reference as string, row.amountSek ?? 0]),
      )
      const outcome = await reconcileOrders({
        payments: result.readyToSettle.map((reference) => ({
          reference,
          amountSek: byReference.get(reference) ?? 0,
        })),
      })

      toast.success(
        outcome.skipped.length === 0
          ? `${outcome.settled.length} beställningar avbockade och koderna utskickade.`
          : `${outcome.settled.length} avbockade, ${outcome.skipped.length} hoppades över.`,
      )
      onClose()
    } catch (caught) {
      toast.error(authErrorMessage(caught))
      setBusy(false)
    }
  }

  /**
   * Laddar ned en exempelrapport byggd av lagets egna provbeställningar.
   *
   * Avstämningen var den enda funktionen som inte gick att visa upp, av den
   * banala anledningen att den kräver en fil man inte har. Filen som byggs
   * här har riktiga referenser ur listan bakom, så avbockningen efteråt gör
   * något på riktigt – och den innehåller med flit en rad med fel belopp och
   * en utan referens, eftersom vyns hela poäng är att skilja på utfallen.
   */
  function downloadSample() {
    const csv = sampleSwishReport(orders)
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'swish-exempelrapport.csv'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Exempelrapporten är nedladdad. Välj den med Välj rapportfil.')
  }

  const ready = result?.readyToSettle.length ?? 0
  const readySum = (result?.rows ?? [])
    .filter((row) => row.status === 'matched')
    .reduce((sum, row) => sum + (row.amountSek ?? 0), 0)

  return (
    <Modal
      open
      onClose={onClose}
      title="Stäm av mot Swish-rapporten"
      footer={
        <div className="flex gap-2">
          <Button variant="quiet" fullWidth onClick={onClose}>
            Avbryt
          </Button>
          <Button
            fullWidth
            disabled={ready === 0}
            loading={busy}
            onClick={() => void handleSettle()}
          >
            {ready === 0 ? 'Inget att bocka av' : `Bocka av ${ready} · ${formatSek(readySum)}`}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <InfoNote>
          Ladda ned transaktionsrapporten från Swish-portalen och välj filen här. Den läses i din
          webbläsare – ingenting skickas vidare utom de beställningar som ska bockas av.
        </InfoNote>

        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv,text/plain"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) void handleFile(file)
          }}
        />

        <Button
          variant="outline"
          fullWidth
          icon={<FileUp size={17} />}
          onClick={() => inputRef.current?.click()}
        >
          {fileName ? `Byt fil (${fileName})` : 'Välj rapportfil'}
        </Button>

        {/* Bara för provdata. En lagledare med riktiga beställningar ska inte
            kunna ladda ned en fil som ser ut som en bankrapport. */}
        {isTest && (
          <div className="rounded-card border-2 border-dashed border-warning/40 px-4 py-3">
            <p className="text-sm text-muted">
              Provläge. Har du ingen Swish-fil att prova med kan du ladda ned en som byggts av
              lagets provbeställningar.
            </p>
            <Button
              size="sm"
              variant="quiet"
              className="mt-2"
              icon={<Download size={15} />}
              onClick={downloadSample}
            >
              Ladda ned exempelrapport
            </Button>
          </div>
        )}

        {readError && <ErrorNote>{readError}</ErrorNote>}
        {result?.problem && <ErrorNote>{result.problem}</ErrorNote>}

        {result && !result.problem && (
          <>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ['matched', 'success'],
                  ['amountMismatch', 'danger'],
                  ['unknownReference', 'warning'],
                  ['noReference', 'neutral'],
                  ['alreadyPaid', 'neutral'],
                  ['cancelled', 'neutral'],
                  ['notAPayment', 'neutral'],
                ] as const
              )
                .filter(([status]) => result.counts[status] > 0)
                .map(([status, tone]) => (
                  <Badge key={status} tone={tone}>
                    {MATCH_LABELS[status]}: {result.counts[status]}
                  </Badge>
                ))}
            </div>

            {result.counts.amountMismatch > 0 && (
              <p className="flex items-start gap-2 text-sm text-danger">
                <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden />
                Betalningar med fel belopp bockas inte av. De behöver tittas på för hand.
              </p>
            )}

            <ul className="max-h-72 space-y-1.5 overflow-y-auto">
              {result.rows.map((row) => (
                <ReportRowItem key={row.line} row={row} />
              ))}
            </ul>
          </>
        )}
      </div>
    </Modal>
  )
}

function ReportRowItem({ row }: { row: ReportRow }) {
  const good = row.status === 'matched'
  const bad = row.status === 'amountMismatch'

  return (
    <li
      className={cn(
        'flex items-center gap-2.5 rounded-card px-3 py-2 text-sm',
        good ? 'bg-success-soft' : bad ? 'bg-danger-soft' : 'bg-surface-2',
      )}
    >
      {good ? (
        <CheckCircle2 size={16} className="shrink-0 text-success" aria-hidden />
      ) : (
        <span className="w-4 shrink-0 text-center text-xs text-faint">{row.line}</span>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">
          {row.reference ?? row.message ?? '– tomt meddelande –'}
          {row.buyerName ? ` · ${row.buyerName}` : ''}
        </p>
        {bad && (
          <p className="text-xs text-danger">
            Betalt {formatSek(row.amountSek ?? 0)}, beställningen är på{' '}
            {formatSek(row.expectedSek ?? 0)}
          </p>
        )}
        {!good && !bad && <p className="truncate text-xs text-muted">{MATCH_LABELS[row.status]}</p>}
      </div>

      <span className="shrink-0 tabular-nums text-muted">{formatSek(row.amountSek ?? 0)}</span>
    </li>
  )
}
